import React from 'react';
import { AlertTriangle, DollarSign, XCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/UI/alert-dialog';

/**
 * ValidationModal — shown when strategy parameters are missing/invalid or capital is insufficient.
 *
 * @param {boolean} open - Whether the modal is visible
 * @param {function} onClose - Called when user clicks "Cancel" (discard edits)
 * @param {function} onUpdate - Called when user clicks "Update Strategy" (stay on form)
 * @param {Array<string>} parameterErrors - List of missing/invalid parameter messages
 * @param {object|null} capitalError - { required, available } if insufficient, else null
 */
const ValidationModal = ({ open, onClose, onUpdate, parameterErrors = [], capitalError = null }) => {
  const hasParamErrors = parameterErrors.length > 0;
  const hasCapitalError = capitalError !== null;

  const fmt = (v) =>
    '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onUpdate()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            Strategy Configuration Issues
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                The following issues need to be resolved before saving:
              </p>

              {hasParamErrors && (
                <div className="rounded-lg border border-red-700/50 bg-red-950/30 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-red-300 mb-2">
                    <XCircle className="h-4 w-4 shrink-0" />
                    Required Parameters Missing or Invalid
                  </div>
                  <ul className="space-y-1 pl-6 text-sm text-red-200/80">
                    {parameterErrors.map((msg, i) => (
                      <li key={i} className="list-disc">{msg}</li>
                    ))}
                  </ul>
                </div>
              )}

              {hasCapitalError && (
                <div className="rounded-lg border border-amber-700/50 bg-amber-950/30 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-300 mb-2">
                    <DollarSign className="h-4 w-4 shrink-0" />
                    Insufficient Capital
                  </div>
                  <div className="space-y-1 text-sm text-amber-200/80">
                    <div className="flex justify-between">
                      <span>Required:</span>
                      <span className="font-medium">{fmt(capitalError.required)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Available:</span>
                      <span className="font-medium">{fmt(capitalError.available)}</span>
                    </div>
                    <div className="flex justify-between border-t border-amber-700/30 pt-1">
                      <span>Shortfall:</span>
                      <span className="font-medium text-red-400">
                        {fmt(capitalError.required - capitalError.available)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onUpdate}>Update Strategy</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ValidationModal;
